import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/**
 * CoreFlow Toast Notification System
 * 
 * Lightweight, non-blocking notifications for success/error/info feedback.
 * Toasts auto-dismiss after a configurable duration.
 * 
 * Usage:
 *   const toast = useToast();
 *   toast.success("Contact saved");
 *   toast.error("Failed to save contact");
 *   toast.info("Syncing...");
 */

const ToastContext = createContext();
export const useToast = () => useContext(ToastContext);

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    if (timers.current[id]) clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((type, message, durationOrOptions = 4000) => {
    // Backward compatible: 3rd arg may be a number (duration ms) OR an options
    // object { duration, action: { label, onClick } }. Existing callers that
    // pass a plain number continue to work unchanged.
    let duration;
    let action = null;
    if (typeof durationOrOptions === "object" && durationOrOptions !== null) {
      duration = durationOrOptions.duration != null ? durationOrOptions.duration : 4000;
      action = durationOrOptions.action || null;
    } else {
      duration = durationOrOptions;
    }
    const id = ++toastId;
    setToasts(prev => {
      // Keep max 5 toasts visible
      const next = [...prev, { id, type, message, action }];
      return next.length > 5 ? next.slice(-5) : next;
    });
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const success = useCallback((msg, arg) => add("success", msg, arg == null ? 2000 : arg), [add]);
  const error = useCallback((msg, arg) => add("error", msg, arg == null ? 6000 : arg), [add]);
  const info = useCallback((msg, arg) => add("info", msg, arg), [add]);
  const warning = useCallback((msg, arg) => add("warning", msg, arg == null ? 5000 : arg), [add]);

  return (
    <ToastContext.Provider value={{ success, error, info, warning, dismiss }}>
      {children}
      {/* Render toasts */}
      {toasts.length > 0 && (
        <div style={{
          position: "fixed", bottom: 20, right: 20,
          display: "flex", flexDirection: "column-reverse", gap: 8,
          zIndex: 99999, pointerEvents: "none",
        }}>
          {toasts.map((t, i) => (
            <Toast key={t.id} toast={t} onDismiss={() => dismiss(t.id)} index={i} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

const TOAST_STYLES = {
  success: { bg: "rgba(0, 214, 143, 0.12)", border: "rgba(0, 214, 143, 0.3)", color: "#00A870", icon: "✓" },
  error: { bg: "rgba(229, 57, 53, 0.10)", border: "rgba(229, 57, 53, 0.25)", color: "#E53935", icon: "✕" },
  warning: { bg: "rgba(255, 181, 71, 0.12)", border: "rgba(255, 181, 71, 0.3)", color: "#E09600", icon: "⚠" },
  info: { bg: "rgba(64, 89, 173, 0.10)", border: "rgba(64, 89, 173, 0.25)", color: "#FAFAFA", icon: "ℹ" },
};

function Toast({ toast, onDismiss, index }) {
  const s = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
  return (
    <div
      style={{
        pointerEvents: "auto",
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 16px",
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        border: `1.5px solid ${s.border}`,
        borderRadius: 12,
        boxShadow: "0 4px 20px rgba(217,219,221,0.12), 0 1px 4px rgba(0,0,0,0.20)",
        minWidth: 260, maxWidth: 400,
        animation: "toastSlideIn 0.25s ease-out",
        cursor: "pointer",
        transition: "opacity 0.2s",
      }}
      onClick={onDismiss}
      title="Click to dismiss"
    >
      {/* Icon */}
      <div style={{
        width: 22, height: 22, borderRadius: 7,
        background: s.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 400, color: s.color,
        flexShrink: 0,
      }}>
        {s.icon}
      </div>
      {/* Message */}
      <div style={{
        fontSize: 13, fontWeight: 400, color: "#FAFAFA",
        lineHeight: 1.4, flex: 1,
      }}>
        {toast.message}
      </div>
      {/* Optional action button (e.g. "Undo") */}
      {toast.action && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            try { toast.action.onClick(); } catch (_e) {}
            onDismiss();
          }}
          style={{
            marginLeft: 12,
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid rgba(217,219,221,0.25)",
            background: "transparent",
            color: "inherit",
            fontSize: 11,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            flexShrink: 0,
          }}
        >
          {toast.action.label}
        </button>
      )}
      {/* Dismiss X */}
      <div style={{
        fontSize: 14, color: "#FAFAFA", cursor: "pointer",
        padding: "0 2px", fontWeight: 300, flexShrink: 0,
      }}>
        ×
      </div>
      {/* Animation keyframes */}
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default ToastProvider;
